import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Star } from "lucide-react";
import { api } from "@/lib/api";
import { Card, Field, TextInput, TextArea, Toggle, SaveButton } from "./kit";

export default function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [embeds, setEmbeds] = useState({ trustpilot_embed: "", google_embed: "" });

  const load = () => {
    api.get("/admin/testimonials").then(({ data }) => setItems(data));
    api.get("/admin/content/embeds").then(({ data }) => setEmbeds(data.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setT = (id, next) => setItems((arr) => arr.map((t) => (t.id === id ? next : t)));

  const toggleVisible = async (t) => {
    setT(t.id, { ...t, visible: !t.visible });
    await api.put(`/admin/testimonials/${t.id}`, { ...t, visible: !t.visible });
  };

  const remove = async (t) => {
    await api.delete(`/admin/testimonials/${t.id}`);
    toast.success("Review removed");
    load();
  };

  const add = async () => {
    await api.post("/admin/testimonials", { quote: "", name: "", country: "", rating: 5, visible: true });
    load();
  };

  const saveEmbeds = () => {
    api.put("/admin/content/embeds", { data: embeds }).then(() => toast.success("Embed codes saved"));
  };

  return (
    <div className="space-y-6" data-testid="admin-testimonials-manager">
      <Card title="Testimonials Manager" desc="Add, edit, hide or remove client review cards shown in the scrolling strip.">
        <div className="space-y-4">
          {items.map((t) => (
            <div key={t.id} className="rounded-xl border border-white/10 bg-[#0A0F2C]/40 p-4" data-testid={`testimonial-row-${t.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-0.5">
                  {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} size={13} className="text-amber-400 fill-amber-400" />)}
                </div>
                <div className="flex items-center gap-3">
                  <Toggle label="Visible" checked={t.visible !== false} onChange={() => toggleVisible(t)} />
                  <button onClick={() => remove(t)} className="p-1.5 text-slate-500 hover:text-red-400 transition-colors" aria-label="Remove review" data-testid={`testimonial-delete-${t.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Quote" className="sm:col-span-2"><TextArea value={t.quote} onChange={(e) => setT(t.id, { ...t, quote: e.target.value })} /></Field>
                <Field label="First name"><TextInput value={t.name} onChange={(e) => setT(t.id, { ...t, name: e.target.value })} /></Field>
                <Field label="Country"><TextInput value={t.country} onChange={(e) => setT(t.id, { ...t, country: e.target.value })} /></Field>
                <Field label="Rating (1-5)">
                  <select className="admin-input" value={t.rating} onChange={(e) => setT(t.id, { ...t, rating: Number(e.target.value) })}>
                    {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} stars</option>)}
                  </select>
                </Field>
                <div className="flex items-end">
                  <button className="btn btn-sm btn-outline-light w-full" onClick={() => api.put(`/admin/testimonials/${t.id}`, t).then(() => toast.success("Review saved"))}>Save review</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <button className="btn btn-sm btn-outline-light mt-5" onClick={add} data-testid="testimonial-add-button">+ Add review card</button>
      </Card>

      <Card title="Review Widgets" desc="Paste Trustpilot or Google review embed codes — they render live in the reviews section.">
        <Field label="Trustpilot embed code"><TextArea rows={3} value={embeds.trustpilot_embed || ""} onChange={(e) => setEmbeds({ ...embeds, trustpilot_embed: e.target.value })} /></Field>
        <Field label="Google review embed code"><TextArea rows={3} value={embeds.google_embed || ""} onChange={(e) => setEmbeds({ ...embeds, google_embed: e.target.value })} /></Field>
        <SaveButton onClick={saveEmbeds} saving={false} label="Save embed codes" />
      </Card>
    </div>
  );
}
